import { Injectable } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { AmplifyTranslationsService } from './amplify-translations.service';

@Injectable({
  providedIn: 'root'
})
export class LanguageService {

  private readonly languageMapping: { [key: string]: string } = {
    'pl_PL': 'pl',
    'en_US': 'en',
    'pseudo_PL': 'pseudo'
  };

  private readonly reverseLanguageMapping: { [key: string]: string } = {
    'pl': 'pl_PL',
    'en': 'en_US',
    'pseudo': 'pseudo_PL'
  };

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private translateService: TranslateService,
    private amplifyTranslationsService: AmplifyTranslationsService
  ) {}

  /**
   * Get current language from URL or localStorage
   */
  getCurrentLanguage(): string {
    const urlLang = this.route.snapshot.paramMap.get('lang');
    if (urlLang && this.reverseLanguageMapping[urlLang]) {
      return this.reverseLanguageMapping[urlLang];
    }
    return localStorage.getItem('selectedLanguage') || 'pl_PL';
  }

  /**
   * Get current language code for URL
   */
  getCurrentLanguageCode(): string {
    const currentLang = this.getCurrentLanguage();
    return this.languageMapping[currentLang] || 'pl';
  }

  /**
   * Switch language and update URL
   */
  switchLanguage(languageCode: string): void {
    const urlLangCode = this.languageMapping[languageCode];
    if (!urlLangCode) {
      console.error('Unsupported language:', languageCode);
      return;
    }

    try {
      // Get current route without language parameter
      const currentUrl = this.router.url;
      const urlParts = currentUrl.split('/').filter(part => part.length > 0);

      // Remove the language parameter (first part)
      if (urlParts.length > 0 && this.supportedLanguageCodes.includes(urlParts[0])) {
        urlParts.splice(0, 1);
      }

      // Add new language parameter at the beginning
      urlParts.unshift(urlLangCode);

      const newUrl = '/' + urlParts.join('/');

      // Update language in services
      this.translateService.use(languageCode);
      localStorage.setItem('selectedLanguage', languageCode);
      this.amplifyTranslationsService.setAmplifyLanguage(languageCode);

      // Small delay to ensure language change is processed before navigation
      setTimeout(() => {
        this.router.navigateByUrl(newUrl);
      }, 50);
    } catch (error) {
      console.error('Error switching language:', error);
      // Fallback: just update the language without changing URL
      this.translateService.use(languageCode);
      localStorage.setItem('selectedLanguage', languageCode);
      this.amplifyTranslationsService.setAmplifyLanguage(languageCode);
    }
  }

  /**
   * Get supported language codes for URL
   */
  get supportedLanguageCodes(): string[] {
    return Object.keys(this.reverseLanguageMapping);
  }

  /**
   * Get supported full language codes
   */
  get supportedLanguages(): string[] {
    return Object.keys(this.languageMapping);
  }

  /**
   * Check if a language code is supported
   */
  isLanguageSupported(languageCode: string): boolean {
    return this.supportedLanguages.includes(languageCode);
  }

  /**
   * Get language name for display
   */
  getLanguageName(languageCode: string): string {
    const languageNames: { [key: string]: string } = {
      'pl_PL': 'Polski',
      'en_US': 'English',
      'pseudo_PL': 'Pseudo'
    };
    return languageNames[languageCode] || languageCode;
  }

  /**
   * Get flag class for language
   */
  getLanguageFlagClass(languageCode: string): string {
    const flagClasses: { [key: string]: string } = {
      'pl_PL': 'fi-pl',
      'en_US': 'fi-gb',
      'pseudo_PL': 'fi-aq'
    };
    return flagClasses[languageCode] || 'fi-pl';
  }
}
